/**
 * Wire shapes for the Referral Marketing print queue, mirrored EXACTLY from the
 * Digital Services source (src/referral-marketing/print/services/
 * rm-print-shop.service.ts and rm-print-shop-actions.service.ts). The repo-wide
 * tsconfig checks no files, so `npm run typecheck:printshop` is what holds these
 * to the components; a wrong field name still fails only at runtime, so these
 * are copied field-for-field from the server on purpose.
 */

/** Wire values are the snake_case strings; lane order below. */
export type RmPrintStage =
  | 'in_setup'
  | 'sent_to_print'
  | 'printed'
  | 'shipped'
  | 'cancelled';

/** The two stages the forward and the batch moves can target. */
export type RmAdvanceStage = 'printed' | 'shipped';

/** The stages a correction can move back to (never `cancelled`). */
export type RmCorrectionStage = Exclude<RmPrintStage, 'cancelled'>;

/** Whether this issue was personalised, carried forward, or cannot be told apart. */
export type RmPersonalizationLabel =
  | 'personalised'
  | 'carried_forward'
  | 'unknown';

/** Lane order, the same as DS `ABO_VISIBLE_RM_STAGES`. */
export const RM_STAGE_ORDER: RmPrintStage[] = [
  'in_setup',
  'sent_to_print',
  'printed',
  'shipped',
  'cancelled',
];

/** The production lane the stepper draws: `cancelled` is not a step. */
export const RM_STEPPER_STAGES: RmCorrectionStage[] = [
  'in_setup',
  'sent_to_print',
  'printed',
  'shipped',
];

/** Who or what filed the job. `null` on a job filed before the field existed. */
export type RmJobSource = 'member' | 'operator' | 'monthly_batch' | 'hubspot_import';

export type RmJobFormat = 'physical' | 'digital';

export type RmFileKind = 'magazine' | 'covers';

/** Present only when `available` is false. There is no `no-longer-stored`. */
export type RmFileUnavailableReason =
  | 'not-generated'
  | 'not-rendered-yet'
  | 'lookup-failed';

export interface RmFileStatus {
  kind: RmFileKind;
  available: boolean;
  reason?: RmFileUnavailableReason;
  /** Upload facts of an operator-uploaded or imported file; absent on a generated one. */
  name?: string;
  size?: number;
  uploadedBy?: string;
  uploadedAt?: string;
}

/** Never null: an unresolved customer yields { name: 'Unknown member', email: 'unknown' }. */
export interface RmMember {
  name: string;
  email: string;
}

export interface RmIssue {
  /** 3-letter UPPERCASE, e.g. 'SEP'. Not a number, not a full month name. */
  month: string;
  /** 4-digit STRING, e.g. '2026'. */
  year: string;
}

export interface RmPrintJobRow {
  jobId: string;
  member: RmMember;
  issue: RmIssue;
  stage: RmPrintStage;
  /** The sent-to-print stamp, or the in-setup stamp for a job still in setup. */
  filedAt?: string;
  /** Whole days waited, server-computed. `null` means unknown — render it as such, never 0. */
  ageDays: number | null;
  /**
   * Three states, from the DS filing snapshot. `personalized` (below) is the
   * old two-state boolean, kept by DS for compatibility; this replaces it in
   * the UI so `unknown` no longer collapses into "carried forward".
   */
  personalizationLabel: RmPersonalizationLabel;
  personalized: boolean;
  /** Whether this issue's monthly window has closed, per the configured cutoff. */
  windowClosed: boolean;
  hubspotTicketId?: string;
  source: RmJobSource | null;
  format: RmJobFormat | null;
  /** A free string from DS, e.g. 'recurring_manual', 'default_design', 'dmp'. */
  customization: string | null;
  /** Whether a magazine PDF reference is stored (uploaded, imported or generated). */
  hasMagazineFile: boolean;
  /**
   * Whether a covers PDF reference is stored (generated, or the HubSpot
   * import's copy of the Service attachment, which is the covers only).
   * Absent from a DS older than the covers import fix.
   */
  hasCoversFile?: boolean;
  hubspotServiceId?: string;
  /**
   * When the job entered the stage it is in now (ISO 8601): the date each tab
   * sorts by. Missing only when the job carries no date at all.
   */
  stageEnteredAt?: string;
  /**
   * Whole 24h periods since `stageEnteredAt`, server-computed; `null` when
   * unknown. Absent from a DS older than the per-tab date sort.
   */
  stageAgeDays?: number | null;
}

/** The list order DS takes. Left out, the tab's own default applies. */
export type RmQueueOrder = 'oldest' | 'newest';

export interface RmJobLinks {
  proof?: string;
  /** Absent is a stated condition: the monthly site rebuild had not completed at filing. */
  memberSite?: string;
  shippedMagazine?: string;
}

export interface RmActor {
  type: 'member' | 'operator' | 'system';
  id: string;
}

/** The `meta.type` values DS writes on a non-transition or special entry. */
export type RmHistoryMetaType =
  | 'stage_corrected'
  | 'cancelled'
  | 'magazine_uploaded'
  | 'filing_upgraded'
  | 'filing_merged'
  | 'imported'
  | 'import_merged'
  | 'import_reverted';

export interface RmHistoryEntry {
  /** Absent on the creating entry (the job was created directly in `to`). */
  from?: RmPrintStage;
  to: RmPrintStage;
  actor: RmActor;
  /** True only when `meta.type === 'stage_corrected'`. */
  correction: boolean;
  meta?: { type?: RmHistoryMetaType | string } & Record<string, unknown>;
  at?: string;
}

/** What the operator may do NOW, resolved by DS. Render from this, never a local machine. */
export interface RmJobActions {
  /** True only in setup with a magazine or covers file. */
  markReady: boolean;
  markReadyBlockedReason: 'no_print_file' | null;
  cancel: boolean;
  uploadMagazine: boolean;
  /** 'printed' from sent_to_print, 'shipped' from printed, else null (always null in setup). */
  nextStage: RmAdvanceStage | null;
  correctionStages: RmCorrectionStage[];
}

export interface RmJobExternalRefs {
  hubspotServiceId?: string;
  hubspotTicketId?: string;
}

export interface RmJobCancellation {
  reason: string;
  cancelledAt: string;
  cancelledBy: string;
}

export interface RmJobDetail extends RmPrintJobRow {
  customerId: string;
  /** `null` for a monthly-batch or imported job with no DS magazine. */
  magazineId: string | null;
  files: RmFileStatus[];
  links: RmJobLinks;
  /** Same as `actions.nextStage`. */
  allowedNextStage: RmAdvanceStage | null;
  /** Same as `actions.correctionStages`. Backwards-only. */
  allowedCorrectionStages: RmCorrectionStage[];
  actions: RmJobActions;
  externalRefs?: RmJobExternalRefs;
  importRunId?: string;
  cancellation?: RmJobCancellation;
  specialInstructions?: string;
  personalizedAt?: string;
  stageTimestamps?: Partial<Record<RmPrintStage, string>>;
  /** Newest first, as DS sends it. */
  history: RmHistoryEntry[];
}

export type RmJobCounts = Record<RmPrintStage, number> & {
  /** in_setup + sent_to_print + printed + shipped (cancelled excluded). */
  total: number;
  /** The day of the month the personalisation window closes. */
  cutoffDay: number;
};

export interface RmPaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

export interface RmPaginated<T> {
  data: T[];
  meta: RmPaginationMeta;
}

/**
 * How S3 serves the signed file: `inline` renders it in the tab (Preview),
 * `attachment` saves it under its readable name (Download).
 */
export type RmFileDisposition = 'inline' | 'attachment';

export interface RmFileDownload {
  url: string;
  expiresAt: string;
}

/** The 409 body AllExceptionsFilter delivers: discriminator on `code`, not `error`. */
export interface RmFileUnavailableError {
  code: 'file_not_available';
  kind: RmFileKind;
  reason: RmFileUnavailableReason;
}

/** One id of a batch move, in request order. */
export type RmBulkAdvanceResult =
  | { jobId: string; ok: true; stage: RmPrintStage }
  | { jobId: string; ok: false; code: string; message: string };

/** Always a 201: a refused id is an outcome to show, not a failed request. */
export interface RmBulkAdvanceResponse {
  toStage: RmAdvanceStage;
  ok: number;
  refused: number;
  results: RmBulkAdvanceResult[];
}

/** The DS refusal codes the queue's actions and the operator send-to-print answer with. */
export type RmRefusalCode =
  | 'RM_PRINT_JOB_NOT_FOUND'
  | 'RM_PRINT_JOB_STAGE_CONFLICT'
  | 'RM_PRINT_JOB_NO_PRINT_FILE'
  | 'RM_PRINT_JOB_NOT_CANCELLABLE'
  | 'RM_PRINT_JOB_FILE_LOCKED'
  | 'RM_PRINT_JOB_ACTION_FAILED'
  | 'RM_PRINT_FILE_NO_BUCKET'
  | 'RM_PRINT_MAGAZINE_SUPERSEDED'
  | 'RM_PRINT_MAGAZINE_PREVIEW'
  | 'RM_PRINT_MAGAZINE_NOT_DONE'
  | 'RM_PRINT_MAGAZINE_NO_FILES'
  | 'PRINT_FILE_MISSING'
  | 'PRINT_FILE_TOO_LARGE'
  | 'PRINT_FILE_NOT_PDF';
